import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { X, User, Lock, Save, ShieldCheck, GraduationCap, School } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const TEACHING_LEVELS = ['Inicial', 'Primaria', 'Secundaria', 'Superior / Universitario'];

// FIX 1: Emojis universales de un solo carácter. Chau a los cuadraditos rotos de Windows.
const AVATARS = ['🤓', '😎', '🎓', '📚', '🦉', '🦊', '🦁', '🚀'];

const emojiFont = { fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", sans-serif' };

export default function ProfileModal({ open, onClose }) {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  // Estados del Perfil
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState('');
  const [avatar, setAvatar] = useState('🤓');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Estados de Seguridad
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    if (open && user) {
      setName(user.name || '');
      setAge(user.age || '');
      setLevels(user.levels || []);
      setSubjects(Array.isArray(user.subjects) ? user.subjects.join(', ') : (user.subjects || ''));
      setAvatar(localStorage.getItem(`avatar_${user.id}`) || '🤓');
      setCurrentPassword('');
      setNewPassword('');
      setActiveTab('profile'); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleLevel = (lvl) => {
    setLevels(prev => prev.includes(lvl) ? prev.filter(l => l !== lvl) : [...prev, lvl]);
  };

  const handleAvatarSelect = (emoji) => {
    setAvatar(emoji);
    if (user?.id) localStorage.setItem(`avatar_${user.id}`, emoji);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    try {
      const subjectsArray = subjects.split(',').map(s => s.trim()).filter(s => s);
      await updateProfile({
        name,
        age: String(age),
        levels,
        subjects: subjectsArray
      });
      toast.success('¡Perfil actualizado con éxito!');
    } catch (err) {
      toast.error('Error al actualizar el perfil.');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    setIsUpdatingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('¡Contraseña cambiada con éxito!');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar la contraseña.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {/* FIX 2: max-h-[90dvh] soluciona el recorte en celulares adaptándose a la barra de direcciones */}
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl w-[95vw] max-h-[90dvh] p-0 flex flex-col md:flex-row overflow-hidden shadow-2xl [&>button]:hidden">
        
        {/* Menú Lateral (Tabs) shrink-0 evita que se aplaste */}
        <div className="w-full md:w-64 bg-slate-950 border-b md:border-b-0 md:border-r border-slate-800 p-4 md:p-6 flex flex-col gap-2 shrink-0">
          <div className="flex items-center justify-between md:hidden mb-2">
            <DialogTitle className="text-lg font-bold">Configuración</DialogTitle>
            <button onClick={onClose} className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white">
              <X size={16} />
            </button>
          </div>

          <div className="hidden md:flex flex-col items-center justify-center mb-6 pt-4">
            <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center text-4xl mb-3 shadow-inner border border-slate-700" style={emojiFont}>
              {avatar}
            </div>
            <p className="font-bold text-sm text-center truncate w-full px-2">{user?.name}</p>
            <p className="text-[10px] text-blue-400 font-semibold uppercase tracking-wider mt-1">{user?.plan?.replace('_', ' ')}</p>
          </div>

          <div className="flex md:flex-col gap-2 overflow-x-auto custom-scrollbar pb-1 md:pb-0">
            <button
              type="button"
              onClick={() => setActiveTab('profile')}
              className={`flex-1 md:flex-none flex items-center justify-center md:justify-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl text-xs md:text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === 'profile' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent hover:border-slate-700'
              }`}
            >
              <User size={16} /> <span className="hidden md:inline">Perfil y Avatares</span><span className="md:hidden">Perfil</span>
            </button>
            
            <button
              type="button"
              onClick={() => setActiveTab('security')}
              className={`flex-1 md:flex-none flex items-center justify-center md:justify-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl text-xs md:text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === 'security' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20' : 'text-slate-400 hover:bg-slate-800 hover:text-white border border-transparent hover:border-slate-700'
              }`}
            >
              <Lock size={16} /> Seguridad
            </button>
          </div>
        </div>

        {/* Área de Contenido - flex-1 y overflow-y-auto le permiten escrollear libremente el resto de la pantalla */}
        <div className="flex-1 p-5 md:p-8 overflow-y-auto custom-scrollbar pb-8 md:pb-8">
          <div className="hidden md:flex justify-between items-center mb-6">
            <DialogTitle className="text-xl font-bold">
              {activeTab === 'profile' ? 'Datos Personales' : 'Seguridad y Acceso'}
            </DialogTitle>
            <button onClick={onClose} className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit} className="space-y-6 animate-fade-in">
              
              {/* Selector de Avatar */}
              <div>
                <label className="text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 md:mb-3 block">Elegí tu Avatar</label>
                <div className="flex flex-wrap gap-2">
                  {AVATARS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => handleAvatarSelect(emoji)}
                      style={emojiFont}
                      className={`w-10 h-10 md:w-11 md:h-11 rounded-xl text-xl flex items-center justify-center transition-all ${
                        avatar === emoji ? 'bg-blue-600/20 border-2 border-blue-500 scale-110' : 'bg-slate-800 border border-slate-700 hover:bg-slate-700'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nombre Completo</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} required className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Tu Edad (Opcional)</label>
                  <input type="number" value={age} onChange={e => setAge(e.target.value)} placeholder="Ej: 35" className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all" />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <School size={12} className="text-blue-400" /> Niveles en los que enseñás
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TEACHING_LEVELS.map(lvl => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => toggleLevel(lvl)}
                        className={`px-3 py-1.5 rounded-lg text-[11px] md:text-xs font-bold transition-all border ${
                          levels.includes(lvl) ? 'bg-blue-600 text-white border-blue-500 shadow-md' : 'bg-slate-950 text-slate-400 border-slate-700 hover:border-slate-500'
                        }`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <GraduationCap size={12} className="text-blue-400" /> Materias (separadas por coma)
                  </label>
                  <input type="text" value={subjects} onChange={e => setSubjects(e.target.value)} placeholder="Ej: Matemáticas, Historia..." className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all" />
                </div>
              </div>

              <div className="pt-2 md:pt-4 border-t border-slate-800 pb-4">
                <button type="submit" disabled={isUpdatingProfile} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-lg shadow-blue-900/20">
                  {isUpdatingProfile ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
                  Guardar Cambios
                </button>
              </div>
            </form>
          )}

          {activeTab === 'security' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6 animate-fade-in pb-4">
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
                <ShieldCheck className="text-blue-400 shrink-0 mt-0.5" size={20} />
                <p className="text-xs text-blue-200/70 leading-relaxed">
                  Tu correo registrado es <strong className="text-white">{user?.email}</strong>. Si necesitás cambiar tu contraseña, ingresá la actual y la nueva a continuación.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Contraseña Actual</label>
                  <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nueva Contraseña (Mínimo 6)</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all" />
                </div>
              </div>

              <div className="pt-2 md:pt-4 border-t border-slate-800">
                <button type="submit" disabled={isUpdatingPassword} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-lg shadow-blue-900/20">
                  {isUpdatingPassword ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : <Lock size={16} />}
                  Actualizar Contraseña
                </button>
              </div>
            </form>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
